<template>
  <div>
    <div class="message-stream">
      <span v-if="isCursorBlinking" class="blinking-cursor">|</span>
      <div ref="contentRef" :class="['message-marked']"></div>
    </div>
    <div v-if="image" class="rich-image-previewer" @click="closeImage">
      <img
          class="rich-image-preview"
          :src="imageSrc"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import vue from '../../../../../../adapter-vue';
import { customerServicePayloadType } from '../../../../../../interface';
import { parseMarkdown } from './marked'
import { TypeWriter } from "./type-writer";
import { JSONToObject } from "../../../../../../utils";

const { ref, computed, watch, onMounted, onUnmounted } = vue;

interface Props {
  payload: customerServicePayloadType;
}

const props = withDefaults(defineProps<Props>(), {
  payload: () => '',
});

const isCursorBlinking = ref<boolean>(true);
const isStreaming = ref<boolean>(false);
const image = ref(false);
const imageSrc = ref('');
const chunks = ref<string>('');
const isFinished = ref<boolean>(true);
const prevChunksLength = ref<number>(0);
const streamContent = ref<string>('');
const contentRef = ref<HTMLElement>(); // DOM 容器引用

// 设置全局回调函数（只需设置一次）
// @ts-ignore
window.onMarkdownImageClicked = function(href: string) {
  image.value = !image.value;
  imageSrc.value = decodeURIComponent(href);
}
// @ts-ignore
window.onMarkdownMediaLoad = function() {
  // empty implementation
  // 已经用 ResizeObserver 观测高度，这里不用重复通知高度变化
}

/**
 * 智能 DOM 更新（innerHTML + 媒体元素保留）：
 * 1. 先把容器内已有的 img/video/a 元素"摘出来"保存
 * 2. 用 innerHTML 全量替换（速度最快，浏览器原生实现）
 * 3. 替换后，把新 DOM 中对应位置的 img/video/a 替换回旧的真实节点
 *    （避免媒体元素被销毁重建导致闪烁/重新加载）
 */
function smartUpdate(container: HTMLElement, newHTML: string) {
  // Step 1: 收集旧的媒体元素，以 src/href 为 key 保存真实 DOM 节点
  const savedMedia = new Map<string, HTMLElement[]>();
  container.querySelectorAll('img, video, a').forEach((el) => {
    const key = el.getAttribute('src') || el.getAttribute('href') || '';
    if (!key) {
      return;
    }
    if (!savedMedia.has(key)) {
      savedMedia.set(key, []);
    }
    savedMedia.get(key)!.push(el as HTMLElement);
  });

  // 没有需要保留的媒体元素，直接 innerHTML 替换即可
  if (savedMedia.size === 0) {
    container.innerHTML = newHTML;
    return;
  }

  // Step 2: 先把旧媒体节点从 DOM 中摘出来（移到一个临时片段，防止被 innerHTML 销毁）
  const fragment = document.createDocumentFragment();
  savedMedia.forEach((els) => {
    els.forEach((el) => fragment.appendChild(el));
  });

  // Step 3: innerHTML 全量替换（最快）
  container.innerHTML = newHTML;

  // Step 4: 遍历新 DOM 中的媒体元素，如果 src/href 匹配，用旧节点替换新节点
  // 这样旧的 img/video 不会重新加载，a 标签保持原有状态
  const usedCount = new Map<string, number>(); // 记录每个 key 已使用的次数（处理同 src 多次出现）
  container.querySelectorAll('img, video, a').forEach((newEl) => {
    const key = newEl.getAttribute('src') || newEl.getAttribute('href') || '';
    if (!key || !savedMedia.has(key)) {
      return;
    }

    const count = usedCount.get(key) || 0;
    const oldEls = savedMedia.get(key)!;
    if (count < oldEls.length) {
      // 用保存的旧节点替换新创建的节点
      newEl.parentNode?.replaceChild(oldEls[count], newEl);
      usedCount.set(key, count + 1);
    }
  });
}

// ---- 媒体标记预提取 & 即时插入逻辑 ----
// 匹配完整的 Markdown 媒体标记：![alt](url) 图片 和 [text](url) 链接
const MEDIA_MARK_RE = /!?\[[^\]]*\]\([^)]*\)/g;

/**
 * 媒体段信息：记录 chunks 中每个媒体标记的位置和内容
 */
interface MediaSegment {
  start: number;  // 媒体标记在 chunks 中的起始位置
  end: number;    // 媒体标记在 chunks 中的结束位置
  mark: string;   // 完整的媒体标记文本，如 ![alt](url)
}

// 从 chunks 预扫描出的所有完整媒体标记
let mediaSegments: MediaSegment[] = [];
// 记录已经即时插入的媒体标记索引，避免重复插入
let insertedMediaIndex = 0;
// 纯文本累计长度（不含已插入的媒体标记），用于判断 TypeWriter 输出到哪了
let pureTextLength = 0;
// 记录 chunks 末尾被截留的不完整媒体标记文本（等下一批 chunks 补全）
let pendingTail = '';
// 记录上一次已处理（送入 TypeWriter + 截留）的 chunks 长度
let processedChunksLength = 0;

/**
 * 从完整 chunks 中预扫描所有媒体标记的位置。
 * 每次 chunks 更新时调用，会发现新补全的完整媒体标记。
 */
function scanMediaSegments(fullText: string) {
  mediaSegments = [];
  MEDIA_MARK_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = MEDIA_MARK_RE.exec(fullText)) !== null) {
    mediaSegments.push({
      start: match.index,
      end: match.index + match[0].length,
      mark: match[0],
    });
  }
}

/**
 * 检测文本末尾是否有未闭合的 Markdown 媒体标记开头。
 * 用于判断 chunks 末尾是否有被截断的媒体标记，需要等下一批补全。
 * 返回未闭合部分的起始位置（相对于 text），如果没有则返回 -1。
 *
 * 例如：
 *   "一些文字![alt](https://xxx"  → 返回 "![alt](https://xxx" 的起始位置
 *   "一些文字![alt"               → 返回 "![alt" 的起始位置
 *   "一些文字完整内容"             → 返回 -1
 */
function findIncompleteTail(text: string): number {
  // 只检查末尾一小段，避免长文本性能问题
  const checkLen = 500;
  const startPos = Math.max(0, text.length - checkLen);
  const tail = text.slice(startPos);

  // 从后往前找最后一个 ![ 或独立的 [（非 ![ 前缀的链接）
  // 策略：找到最后一个可能的媒体标记起始位置，检查它是否闭合
  let lastImgStart = tail.lastIndexOf('![');
  let lastLinkStart = -1;

  // 找最后一个 [ 但不是 ![ 的一部分（用于 [text](url) 链接）
  for (let i = tail.length - 1; i >= 0; i--) {
    if (tail[i] === '[' && (i === 0 || tail[i - 1] !== '!')) {
      lastLinkStart = i;
      break;
    }
  }

  // 取最靠后的那个起始位置
  const candidatePos = Math.max(lastImgStart, lastLinkStart);
  if (candidatePos === -1) return -1;

  // 从候选位置开始，检查是否有完整闭合的 ] 和 )
  const candidate = tail.slice(candidatePos);

  // 检查是否有完整的 [...](...) 结构
  const bracketClose = candidate.indexOf(']');
  if (bracketClose === -1) {
    // [ 还没闭合
    return startPos + candidatePos;
  }

  // ] 后面必须紧跟 (
  if (bracketClose + 1 >= candidate.length || candidate[bracketClose + 1] !== '(') {
    // ] 后面没有 (，可能不是媒体标记，不截留
    return -1;
  }

  // 有 ]( 了，检查 ) 是否闭合
  const parenClose = candidate.indexOf(')', bracketClose + 2);
  if (parenClose === -1) {
    // ]( 后面的 url 还没闭合
    return startPos + candidatePos;
  }

  // 完整闭合了，不需要截留
  return -1;
}

/**
 * 将 chunks 中的新增内容拆分为：只保留纯文本部分（去掉已识别的完整媒体标记）。
 * @param text - 要处理的文本
 * @param offsetInChunks - 这段文本在完整 chunks 中的起始偏移
 * @returns 去掉媒体标记后的纯文本
 */
function stripMediaMarks(text: string, offsetInChunks: number): string {
  let result = '';
  let cursor = offsetInChunks;
  const end = offsetInChunks + text.length;

  for (const seg of mediaSegments) {
    // 跳过已经在之前处理过的媒体标记
    if (seg.end <= cursor) continue;
    // 超出本次新增范围，后面的也不用看了
    if (seg.start >= end) break;

    // 媒体标记前面的纯文本
    if (seg.start > cursor) {
      result += text.substring(cursor - offsetInChunks, seg.start - offsetInChunks);
    }
    // 跳过媒体标记本身（不加入纯文本）
    cursor = Math.min(seg.end, end);
  }

  // 剩余的纯文本
  if (cursor < end) {
    result += text.substring(cursor - offsetInChunks);
  }

  return result;
}

/**
 * 检查 TypeWriter 的纯文本输出进度，当输出到某个媒体标记的位置时，
 * 立即将完整的媒体标记插入 streamContent，不走逐字输出。
 */
function tryInsertPendingMedia() {
  while (insertedMediaIndex < mediaSegments.length) {
    const seg = mediaSegments[insertedMediaIndex];
    // 计算该媒体标记之前有多少纯文本字符
    let prevMediaLen = 0;
    for (let i = 0; i < insertedMediaIndex; i++) {
      prevMediaLen += mediaSegments[i].mark.length;
    }
    const pureTextBeforeThisMark = seg.start - prevMediaLen;

    // 当 TypeWriter 输出的纯文本长度达到该媒体标记的位置时，立即插入
    if (pureTextLength >= pureTextBeforeThisMark) {
      streamContent.value += seg.mark;
      insertedMediaIndex++;
    } else {
      break;
    }
  }
}

/**
 * 执行实际的 DOM 渲染
 */
function doRender() {
  if (!contentRef.value) {
    return;
  }
  const html = parseMarkdown(streamContent.value);
  smartUpdate(contentRef.value, html);
  emits('heightChanged');
}

/**
 * 监听 streamContent 变化，直接渲染（不再需要缓冲延迟）。
 * 因为媒体标记是完整插入的，streamContent 中不会出现半截的 ![alt](url
 */
watch(streamContent, () => {
  if (!contentRef.value) {
    return;
  }
  doRender();
});

const emits = defineEmits(['heightChanged']);
// 不支持 ResizeObserver 的浏览器太老旧，默认不处理了
const canIUseResizeObserver = typeof ResizeObserver === 'undefined' ? false : true;

let observer;
let prevHeight;

const typeWriter = new TypeWriter({
  onTyping: (item: string) => {
    // TypeWriter 只输出纯文本，累计纯文本长度
    pureTextLength += item.length;
    streamContent.value += item;
    // 检查是否到了某个媒体标记的位置，是则立即插入完整标记
    tryInsertPendingMedia();
  },
  onComplete() {
    isStreaming.value = false;
    // 完成时确保所有剩余媒体标记都已插入（防御边界情况）
    while (insertedMediaIndex < mediaSegments.length) {
      streamContent.value += mediaSegments[insertedMediaIndex].mark;
      insertedMediaIndex++;
    }
  },
});

function startStreaming(content: string[]) {
  if (!isStreaming.value) {
    isStreaming.value = true;
    typeWriter.add(content);
    typeWriter.start();
  } else {
    typeWriter.add(content);
  }
}

watch(() => props.payload, (newValue: string, oldValue: string) => {
      if (newValue === oldValue) {
        return;
      }

      const _payloadObject = JSONToObject(props.payload);
      if (!_payloadObject.chunks) {
        return;
      }
      chunks.value = Array.isArray(_payloadObject.chunks) ? _payloadObject.chunks.join('') : _payloadObject.chunks;
      isFinished.value = _payloadObject.isFinished === 1;

      // 每次 chunks 更新时，重新扫描所有完整的媒体标记
      // （因为上一批截断的标记可能在这一批补全了）
      scanMediaSegments(chunks.value);

      // hide blinking cursor
      if (chunks.value.length > 0) {
        isCursorBlinking.value = false;
      }

      if (newValue && !oldValue && isFinished.value) {
        // disable typeWriter style or history message first load
        // 历史消息直接全量渲染，不需要拆分
        streamContent.value = chunks.value;
        prevChunksLength.value = chunks.value.length;
        processedChunksLength = chunks.value.length;
        pendingTail = '';
        // 标记所有媒体都已插入
        insertedMediaIndex = mediaSegments.length;
        pureTextLength = chunks.value.length;
      } else {
        // 判断长度是为了防御编辑的内容回退和内容重复的异常 case
        if (chunks.value.length > prevChunksLength.value) {
          prevChunksLength.value = chunks.value.length;

          // 本次要处理的文本 = 上次截留的不完整尾部 + 本次新增内容
          // 从 processedChunksLength 开始取（而非 prevChunksLength），因为截留部分还没处理
          const unprocessed = chunks.value.slice(processedChunksLength);

          // 检测末尾是否有未闭合的媒体标记（被截断了，需要等下一批补全）
          let safeText = unprocessed;
          pendingTail = '';

          if (!isFinished.value) {
            // 流还没结束，检查末尾是否有截断的媒体标记
            const incompletePos = findIncompleteTail(chunks.value);
            if (incompletePos !== -1 && incompletePos >= processedChunksLength) {
              // 截留末尾不完整的部分，只处理前面安全的部分
              const safeEnd = incompletePos - processedChunksLength;
              safeText = unprocessed.slice(0, safeEnd);
              pendingTail = unprocessed.slice(safeEnd);
            }
          } else {
            // 流已结束，不再截留，全部处理
            pendingTail = '';
          }

          // 从安全文本中去掉已识别的完整媒体标记，只把纯文本送入 TypeWriter
          if (safeText.length > 0) {
            const pureText = stripMediaMarks(safeText, processedChunksLength);
            processedChunksLength += safeText.length;
            if (pureText.length > 0) {
              startStreaming([pureText]);
            }
          }
        }
      }
    }, {
      deep: true,
      immediate: true,
    },
);

onMounted(() => {
  // 如果 streamContent 在 mounted 前已有值（如历史消息），需要在此时渲染
  if (contentRef.value && streamContent.value) {
    const html = parseMarkdown(streamContent.value);
    smartUpdate(contentRef.value, html);
  }

  if (canIUseResizeObserver && contentRef.value) {
    observer = new ResizeObserver(entries => {
      for (let entry of entries) {
        observeHeightChanged(entry.contentRect.height);
      }
    });
    // 开始观察
    observer.observe(contentRef.value);
  }
});

onUnmounted(() => {
  if (canIUseResizeObserver) {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
  }
});

const observeHeightChanged = (newHeight) => {
  if (prevHeight !== newHeight) {
    prevHeight = newHeight;
    emits('heightChanged');
  }
};

const closeImage = () => {
  image.value = !image.value;
  imageSrc.value = '';
};

</script>
<style lang="scss" scoped>
@import "../../../../style/common.scss";
.message-stream {
  overflow-wrap: break-word;
  word-break: normal;
  white-space: normal;
  font-size: 14px;
  user-select: text;
}

:deep(.message-marked li) {
  list-style: disc;
  margin-left: 1.5em;
}

.blinking-cursor {
  animation: blink 0.8s step-end infinite;
  color: #000;
}

@keyframes blink {
  from, to { opacity: 1; }
  50% { opacity: 0; }
}
</style>
